import { isSameDay, roundToNearestMinutes } from 'date-fns';

import { DEFAULT_DELIVERY_TIMESLOT_DURATION_IN_HOURS } from '@klareospace/pharos-lib-types';
import type { Site } from '@klareospace/pharos-lib-types';
import { isInvertedTimeSlot } from '@klareospace/pharos-lib-utils';
import { setHoursMinutes } from '@klareospace/pharos-lib-web-utils';
import type { DateRangePreset, DateRangeValue, TimeOfDay } from '@klareospace/pharos-lib-web-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const MS_PER_HOUR = 60 * 60 * 1000;
const QUARTER_HOUR_MINUTES = 15;
const LAST_MINUTE_OF_DAY: TimeOfDay = { hour: 23, minute: 59 };

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Seeds a fresh `DateRangeFilter` value for `preset`: start is "now" rounded up to the next
 * quarter hour, end is that start plus the site's own `deliveryTimeslotDurationInHours` (falling
 * back to the system default) — clamped to 23:59 the same day when that duration would otherwise
 * wrap past midnight. Shared by the Route information pickup timeslot (`preset: 'custom'`) and the
 * Scheduling section's delivery timeslot (`preset: 'today'`).
 *
 * When rounding "now" up to the next quarter hour pushes `from` into the next calendar day (e.g.
 * 23:50 rounds to 00:00 tomorrow) and the requested preset is `'today'`, the returned preset is
 * corrected to `'tomorrow'` instead — otherwise the `DateRangeFilter` chip would read "Today" for a
 * range that actually starts tomorrow. `'custom'` (and any other preset) is left untouched, since
 * only the day-preset chip label depends on the preset matching the range's actual day.
 */
export function buildDefaultTimeSlotValue(site: Site | null, preset: DateRangePreset): DateRangeValue {
  const durationInHours = site?.deliveryTimeslotDurationInHours ?? DEFAULT_DELIVERY_TIMESLOT_DURATION_IN_HOURS;
  const now = new Date();
  const from = roundUpToQuarterHour(now);
  const to = clampEndOfDay(from, new Date(from.getTime() + durationInHours * MS_PER_HOUR));
  const resolvedPreset = preset === 'today' && !isSameDay(now, from) ? 'tomorrow' : preset;
  return { preset: resolvedPreset, range: { from, to } };
}

/**
 * True when a `DateRangeValue`'s own range has start >= end. Single source for the inversion check
 * shared by the Route information pickup timeslot (`isPickupTimeSlotInverted`) and the Scheduling
 * delivery timeslot (`isSchedulingTimeSlotInverted`) — both wrap this with their own slice's
 * enabled/unset guard before delegating here.
 */
export function isDateRangeValueInverted(value: DateRangeValue): boolean {
  const { from, to } = value.range;
  return isInvertedTimeSlot({ end: to, start: from });
}

// ─── Helpers (private) ──────────────────────────────────────────────────────────

/** Rounds `date` up to the next quarter hour; a value already on a quarter boundary is left unchanged. */
function roundUpToQuarterHour(date: Date): Date {
  return roundToNearestMinutes(date, { nearestTo: QUARTER_HOUR_MINUTES, roundingMethod: 'ceil' });
}

/** Clamps `end` to 23:59 on `start`'s own day when the added duration pushed it past midnight. */
function clampEndOfDay(start: Date, end: Date): Date {
  const crossedMidnight =
    end.getFullYear() !== start.getFullYear() ||
    end.getMonth() !== start.getMonth() ||
    end.getDate() !== start.getDate();
  return crossedMidnight ? setHoursMinutes(start, LAST_MINUTE_OF_DAY) : end;
}
